import { forwardRef, useId, type SelectHTMLAttributes } from "react";
import { FieldMarker } from "@/components/FieldMarker";

// Labeled <select> with optional inline error and a placeholder option, the
// dropdown sibling of TextField (DRY) for the enum/lookup pickers introduced by
// BRK-S020 (relationship status, entity type, industry, CMS, servicing broker).
// Full-width and stacked for the mobile-first layout; the error is wired via
// aria-describedby + aria-invalid like TextField.
export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  error?: string;
  /** Shown as a disabled-by-default first option when set (e.g. "Select…"). */
  placeholder?: string;
  /** Show a muted "(optional)" marker (the counterpart to the required "*"). */
  optional?: boolean;
}

// REF-FORWARDING (BRK-S177): the bid-result reminder's deep link has to leave
// the cursor in the outcome control, and a caller that reached for it with
// `document.getElementById` would be addressing a shared component by an id it
// does not own. Forwarding the ref is the smaller, typed answer.
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField(
    { label, options, error, placeholder, id, optional, ...selectProps },
    ref,
  ) {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const errorId = `${fieldId}-error`;

    return (
      <div className="flex w-full flex-col gap-1.5 text-left">
        <span className="flex items-baseline gap-1.5">
          <label
            htmlFor={fieldId}
            className="text-sm font-medium text-brk-text"
          >
            {label}
          </label>
          <FieldMarker required={selectProps.required} optional={optional} />
        </span>
        <select
          ref={ref}
          id={fieldId}
          className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...selectProps}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={errorId} className="text-sm text-brk-danger">
            {error}
          </p>
        )}
      </div>
    );
  },
);
